import BigNumber from 'bignumber.js';
import {
  BESortDirection,
  CreateTimeSeriesQueryInterface,
  DataPointInterface,
  DateTimeIntervalInterface,
  GetDeviceProductionPlanDetailResponse,
  INTERPOLATION_CONSTANT_VALUE_ZERO,
  InterpolationType,
  INTERVAL_15_MINUTES,
  LabelInterface,
  MarketPeriodInterface,
  MetricSpecInterface,
  MonitoredResourceSpecBasicInterface,
  MonitoredResourceType,
  MonitoredResourceTypeLabel,
  PaginationInterface,
  SeriesAligner,
  SeriesAlignmentFilterInterface,
  TimeIntervalInterface,
  TimeSeriesInterface,
  TimeSeriesQueryInterface,
  TransformationFilterInterface
} from 'features/dispatcher/dispatcherTypes';
import { BalanceGroupDetailInterface } from 'features/eai/eaiTypes';
import { TFunction } from 'i18next';
import _, { toNumber } from 'lodash';
import { ChartPoint, KeyedObject } from 'models';
import moment from 'moment/moment';
import { UnitType } from 'utils/characteristic';
import {
  DateFormat,
  FRACTION_DIGITS_THREE_DECIMAL,
  IDM_PRICE_TYPE,
  MetricValueType,
  PlannedImbalanceType
} from 'utils/constants';
import {
  getCurrentMarketPeriod,
  getMarketDayInterval,
  getMarketDayIntervalFromMarketPeriods
} from 'utils/marketPeriods/marketPeriods';

const getResourceType = (
  resourceTypes: MonitoredResourceSpecBasicInterface[],
  type: string
): string => {
  const toReturn = resourceTypes.find(
    (resourceType) => resourceType.type === type
  );
  return toReturn !== undefined ? toReturn.type : '';
};

const getTimeInterval = (
  startDate: Date,
  endDate: Date
): TimeIntervalInterface => {
  return {
    startDateTime: moment(startDate).format(DateFormat.REST_DATETIME_FORMAT),
    endDateTime: moment(endDate).format(DateFormat.REST_DATETIME_FORMAT)
  };
};

// TODO: delete
export const getStartOfDay = (dateDay?: Date) => {
  const startDate = dateDay ? new Date(dateDay) : new Date();
  startDate.setHours(0);
  startDate.setMinutes(0);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);
  return startDate;
};

// TODO: delete
export const getEndOfDay = (dateDay?: Date) => {
  const startDate = dateDay ? new Date(dateDay) : new Date();
  startDate.setHours(24);
  startDate.setMinutes(0);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);
  return startDate;
};

export const getTimeIntervalFromMarketPeriod = (
  marketPeriod: MarketPeriodInterface
): TimeIntervalInterface => {
  return {
    startDateTime: marketPeriod.startDateTime,
    endDateTime: marketPeriod.endDateTime
  };
};

// TODO - do not use local timzeone
const getTimeIntervalForDay = () => {
  const now = moment();

  const startOfDay = now.startOf('day').format(DateFormat.REST_DATETIME_FORMAT);
  const endOfDay = now.endOf('day').format(DateFormat.REST_DATETIME_FORMAT);

  return {
    startDateTime: startOfDay,
    endDateTime: endOfDay
  };
};

// TODO - investigate
export const getTimeIntervalForLastMinute = () => {
  const beforeMinute = new Date();
  beforeMinute.setMinutes(beforeMinute.getMinutes() - 1);

  return getTimeInterval(beforeMinute, new Date());
};

export const getTimeIntervalFromStartOfDayToDatapoint = (
  lastDataPointTime: string | undefined
): TimeIntervalInterface => {
  const today = new Date().toISOString();
  const marketDayInterval = getMarketDayInterval(today);

  return {
    startDateTime: marketDayInterval.marketDayStart,
    endDateTime: lastDataPointTime ?? marketDayInterval.marketDayEnd
  };
};

// TODO - investigate and rework to market day using timestamps instead of HH:MM
export const getTimeIntervalForPreviousAndActualPeriod = (
  marketPeriods: MarketPeriodInterface[],
  lastDataPointTime: string | undefined
): TimeIntervalInterface => {
  const marketDayInterval =
    getMarketDayIntervalFromMarketPeriods(marketPeriods);

  if (!marketDayInterval) {
    throw new Error('invalid market day');
  }
  const startOfDay = marketDayInterval.marketDayStart;
  const endOfDay = marketDayInterval.marketDayEnd;

  // TODO rework to get incremental all day interval (last datapoint start -> end of day)
  if (lastDataPointTime) {
    return {
      startDateTime: lastDataPointTime,
      endDateTime: endOfDay
    };
  }
  // all day
  return {
    startDateTime: startOfDay,
    endDateTime: endOfDay
  };
};

const getDeliveryPointResourceType = (
  resourceTypes: MonitoredResourceSpecBasicInterface[]
): string =>
  getResourceType(resourceTypes, MonitoredResourceType.DELIVERY_POINT);

export const createDeliveryPointPredictionProductionEnergyProductionHXTimeSeriesQuery =
  (deliveryPointId: string, startTime?: string): TimeSeriesQueryInterface => {
    return {
      metric: { type: MetricValueType.predProdEnergyProdHX },
      monitoredResource: {
        type: MonitoredResourceType.DELIVERY_POINT,
        labels: [
          {
            key: MonitoredResourceTypeLabel.DELIVERY_POINT_ID,
            value: deliveryPointId
          }
        ]
      },
      timeInterval: getIncrementalAllDayTimeInterval(startTime)
    };
  };

export const createDevicePredictionProductionEnergyProductionHXTimeSeriesQuery =
  (deviceId: string, startTime?: string): TimeSeriesQueryInterface => {
    return {
      metric: { type: MetricValueType.predProdEnergyProdHX },
      monitoredResource: {
        type: MonitoredResourceType.DEVICE,
        labels: [
          {
            key: MonitoredResourceTypeLabel.DEVICE_ID,
            value: deviceId
          }
        ]
      },
      timeInterval: getIncrementalAllDayTimeInterval(startTime)
    };
  };

export const createDevicePredictionProductionBessEndOfPeriodSocHXTimeSeriesQuery =
  (deviceId: string, startTime?: string): TimeSeriesQueryInterface => {
    return {
      metric: { type: MetricValueType.predProdEndOfPeriodSocHX },
      monitoredResource: {
        type: MonitoredResourceType.DEVICE,
        labels: [
          {
            key: MonitoredResourceTypeLabel.DEVICE_ID,
            value: deviceId
          }
        ]
      },
      timeInterval: getIncrementalAllDayTimeInterval(startTime)
    };
  };

export const createDevicePredictionProductionBessEndOfPeriodStoredHxTimeSeriesQuery =
  (deviceId: string, startTime?: string): TimeSeriesQueryInterface => {
    return {
      metric: { type: MetricValueType.predProdEndOfPeriodEnergyStoredHX },
      monitoredResource: {
        type: MonitoredResourceType.DEVICE,
        labels: [
          {
            key: MonitoredResourceTypeLabel.DEVICE_ID,
            value: deviceId
          }
        ]
      },
      timeInterval: getIncrementalAllDayTimeInterval(startTime)
    };
  };

export const createSystemImbalanceTimeSeriesQuery = (
  prediction: boolean,
  startTime?: string
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: prediction
        ? MetricValueType.predictionSystemImbalanceHX
        : MetricValueType.actualSystemImbalance
    },
    monitoredResource: {
      type: MonitoredResourceType.SYSTEM
    },
    timeInterval: getIncrementalAllDayTimeInterval(startTime),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const createPositiveSystemImbalancePriceHXTimeSeriesQuery = (
  balancingGroupId: string | bigint
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.positiveSystemImbalancePriceHX
    },
    monitoredResource: {
      type: MonitoredResourceType.BALANCING_GROUP,
      labels: [
        {
          key: MonitoredResourceTypeLabel.BALANCING_GROUP_ID,
          value: balancingGroupId
        }
      ]
    },
    timeInterval: getIncrementalAllDayTimeInterval()
  };
};

export const createNegativeSystemImbalancePriceHXTimeSeriesQuery = (
  balancingGroupId: string | bigint
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.negativeSystemImbalancePriceHX
    },
    monitoredResource: {
      type: MonitoredResourceType.BALANCING_GROUP,
      labels: [
        {
          key: MonitoredResourceTypeLabel.BALANCING_GROUP_ID,
          value: balancingGroupId
        }
      ]
    },
    timeInterval: getIncrementalAllDayTimeInterval()
  };
};

export const createPositiveSystemImbalanceProbabilityHxTimeSeriesQuery = (
  balancingGroupId: string | bigint
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.positiveSystemImbalanceProbabilityHX
    },
    monitoredResource: {
      type: MonitoredResourceType.BALANCING_GROUP,
      labels: [
        {
          key: MonitoredResourceTypeLabel.BALANCING_GROUP_ID,
          value: balancingGroupId
        }
      ]
    },
    timeInterval: getIncrementalAllDayTimeInterval(),
    transformationFilter: { unitOfMeasure: UnitType.PERCENT }
  };
};

export const createPredictedPriceTimeSeriesQuery = (
  plannedImbalance: IDM_PRICE_TYPE
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type:
        plannedImbalance === IDM_PRICE_TYPE.IDM15_PRICE
          ? MetricValueType.price15mHX
          : MetricValueType.price60mHX
    },
    monitoredResource: {
      type: MonitoredResourceType.SYSTEM
    },
    timeInterval: getIncrementalAllDayTimeInterval()
  };
};

export const getLatestDataPointForDeliveryPoint = (
  timeSeries: TimeSeriesInterface[],
  deliveryPointId: string
): DataPointInterface | undefined => {
  const dataPoints = getDataPointsForDeliveryPoint(timeSeries, deliveryPointId);
  return sortDataPointsByDateTimeFromDescending(dataPoints).at(0);
};

const getEnergyImbalanceTimeSeriesQuery = (
  balancingGroupId: string | bigint,
  timeInterval: TimeIntervalInterface
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.energyImbalance
    },
    monitoredResource: {
      type: MonitoredResourceType.BALANCING_GROUP,
      labels: [
        {
          key: MonitoredResourceTypeLabel.BALANCING_GROUP_ID,
          value: balancingGroupId
        }
      ]
    },
    seriesAlignmentFilter: {
      alignmentPeriod: INTERVAL_15_MINUTES,
      seriesAligner: SeriesAligner.ALIGN_SUM,
      interpolation: InterpolationType.INTERPOLATE_CONSTANT,
      interpolationConstantValue: INTERPOLATION_CONSTANT_VALUE_ZERO
    },
    transformationFilter: { unitOfMeasure: UnitType.MWH },
    timeInterval
  };
};

export const createEnergyImbalanceForHxPlanningTimeSeriesQuery = (
  balancingGroupId: string | bigint,
  marketPeriods: MarketPeriodInterface[],
  endOfCurrentMarketPeriod?: string
): TimeSeriesQueryInterface => {
  return getEnergyImbalanceTimeSeriesQuery(
    balancingGroupId,
    getTimeIntervalFromStartOfDayToDatapoint(endOfCurrentMarketPeriod)
  );
};

export const createEnergyImbalanceForBalanceGroupTimeSeriesQuery = (
  balancingGroupId: string | bigint,
  marketPeriods: MarketPeriodInterface[],
  lastDataPointTime?: string
): TimeSeriesQueryInterface => {
  return getEnergyImbalanceTimeSeriesQuery(
    balancingGroupId,
    getTimeIntervalForPreviousAndActualPeriod(marketPeriods, lastDataPointTime)
  );
};

export const createActiveEnergyDeviceTimeSeriesQuery = (
  deviceId: string | bigint,
  timeInterval: TimeIntervalInterface
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.activeEnergyBalance
    },
    monitoredResource: {
      type: MonitoredResourceType.DEVICE,
      labels: [{ key: MonitoredResourceTypeLabel.DEVICE_ID, value: deviceId }]
    },
    timeInterval: timeInterval,
    seriesAlignmentFilter: {
      alignmentPeriod: INTERVAL_15_MINUTES,
      seriesAligner: SeriesAligner.ALIGN_SUM,
      interpolation: InterpolationType.INTERPOLATE_CONSTANT,
      interpolationConstantValue: { doubleValue: 2 }
    },
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const createPlannedImbalanceMetricTimeSeriesQuery = (
  id: string | bigint,
  predictedHorizon: PlannedImbalanceType,
  device = false,
  startDateTime?: string
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type:
        predictedHorizon === PlannedImbalanceType.D1
          ? MetricValueType.plannedImbalanceD1
          : MetricValueType.plannedImbalanceHX
    },
    monitoredResource: {
      type: device
        ? MonitoredResourceType.DEVICE
        : MonitoredResourceType.BALANCING_GROUP,
      labels: [
        {
          key: device
            ? MonitoredResourceTypeLabel.DEVICE_ID
            : MonitoredResourceTypeLabel.BALANCING_GROUP_ID,
          value: id.toString()
        }
      ]
    },
    timeInterval: getIncrementalAllDayTimeInterval(startDateTime),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const getDataPointsForDeviceAndMetric = (
  timeSeries: TimeSeriesInterface[],
  deviceId: string,
  metricSpecType: string,
  metricLabels?: LabelInterface[]
): DataPointInterface[] =>
  getDataPointsForDevice(
    timeSeries.filter(
      (timeSeriesItem) =>
        timeSeriesItem.metric.type === metricSpecType &&
        hasSameLabels(timeSeriesItem.metric.labels, metricLabels)
    ),
    deviceId
  );

export const getLastDataPointForDeviceAndMetricType = (
  timeSeries: TimeSeriesInterface[],
  deviceId: string | bigint,
  metricSpecType: string
): DataPointInterface | undefined => {
  const filtered = timeSeries.filter(
    (timeSeriesItem) => timeSeriesItem.metric.type === metricSpecType
  );

  const dataPoints = getDataPointsForDevice(filtered, deviceId);
  return dataPoints.length > 0 ? dataPoints.at(-1) : undefined;
};

const getDataPointsForDevice = (
  timeSeries: TimeSeriesInterface[],
  deviceId: string | bigint
): DataPointInterface[] => {
  const matchingTimeSeriesItem = timeSeries.find((timeSeriesItem) =>
    timeSeriesItem.monitoredResource.labels?.some(
      (label) =>
        label.key === MonitoredResourceTypeLabel.DEVICE_ID &&
        label.value === deviceId
    )
  );

  return matchingTimeSeriesItem?.dataPoints || [];
};

export const getDataPointsForDeliveryPoint = (
  timeSeries: TimeSeriesInterface[],
  deliveryPointId: string
): DataPointInterface[] => {
  const matchingTimeSeriesItem = timeSeries.find((timeSeriesItem) =>
    timeSeriesItem.monitoredResource.labels?.some(
      (label) =>
        label.key === MonitoredResourceTypeLabel.DELIVERY_POINT_ID &&
        label.value === deliveryPointId
    )
  );

  return matchingTimeSeriesItem?.dataPoints ?? [];
};

export const getDataPointsForBalancingGroup = (
  balancingGroup: BalanceGroupDetailInterface | undefined,
  timeSeries: TimeSeriesInterface[]
): DataPointInterface[] => {
  if (!balancingGroup) return [];

  const matchingTimeSeriesItem = timeSeries.find((timeSeriesItem) =>
    timeSeriesItem.monitoredResource.labels?.some(
      (label) =>
        label.key === MonitoredResourceTypeLabel.BALANCING_GROUP_ID &&
        label.value === balancingGroup.id.toString()
    )
  );

  return matchingTimeSeriesItem?.dataPoints ?? [];
};

export const getMetric = (
  metrics: MetricSpecInterface[],
  metricType: MetricValueType
): MetricSpecInterface | undefined =>
  metrics.find((metric) => metric.type === metricType);

export const createTimeSeriesQuery = (
  metric: MetricSpecInterface,
  monitoredResourceType: MonitoredResourceType,
  monitoredResourceLabels: LabelInterface[],
  timeInterval: TimeIntervalInterface,
  metricLabels?: LabelInterface[],
  transformationFilter?: TransformationFilterInterface,
  seriesAlignmentFilter?: SeriesAlignmentFilterInterface,
  pagination?: PaginationInterface
): TimeSeriesQueryInterface => {
  return {
    metric: { type: metric.type, labels: metricLabels || [] },
    monitoredResource: {
      type: getResourceType(
        metric.monitoredResourceTypes,
        monitoredResourceType
      ),
      labels: monitoredResourceLabels
    },
    timeInterval: timeInterval,
    seriesAlignmentFilter: seriesAlignmentFilter,
    transformationFilter: transformationFilter,
    pagination: pagination
  };
};

export const createTimeSeriesQueryObject = ({
  metric,
  monitoredResourceType,
  monitoredResourceLabels,
  timeInterval,
  metricLabels,
  transformationFilter,
  seriesAlignmentFilter,
  pagination,
  groupFilter
}: CreateTimeSeriesQueryInterface): TimeSeriesQueryInterface => {
  return {
    metric: { type: metric.type, labels: metricLabels || [] },
    monitoredResource: {
      type: getResourceType(
        metric.monitoredResourceTypes,
        monitoredResourceType
      ),
      labels: monitoredResourceLabels
    },
    timeInterval: timeInterval,
    seriesAlignmentFilter: seriesAlignmentFilter,
    transformationFilter: transformationFilter,
    pagination: pagination,
    groupFilter: groupFilter
  };
};

export const getDataPointsForDeliveryPointAndMetric = (
  timeSeries: TimeSeriesInterface[],
  deliveryPointId: string,
  metricType?: string,
  metricLabels?: LabelInterface[]
): DataPointInterface[] => {
  const filteredTimeSeries = metricType
    ? timeSeries.filter(
        (timeSeriesItem) =>
          timeSeriesItem.metric.type === metricType &&
          hasSameLabels(timeSeriesItem.metric.labels, metricLabels)
      )
    : timeSeries;

  return getDataPointsForDeliveryPoint(filteredTimeSeries, deliveryPointId);
};

export const getLastDateTimeToForTimeSeries = (
  timeSeries: TimeSeriesInterface[],
  monitoredResourceType: MonitoredResourceTypeLabel,
  monitoredResourceValue: string
): string | undefined => {
  return timeSeries
    .find((timeSeriesItem) =>
      timeSeriesItem.monitoredResource.labels?.some((label) => {
        const isCorrectLabel = label.key === monitoredResourceType;
        return isCorrectLabel && label.value === monitoredResourceValue;
      })
    )
    ?.dataPoints?.at(-1)?.interval.dateTimeFrom;
};

export const getTimeSeriesForMetricAndLabels = (
  timeSeries: TimeSeriesInterface[],
  monitoredResourceType: MonitoredResourceTypeLabel,
  metricType: string,
  monitoredResourceValue?: string,
  metricLabels?: LabelInterface[]
): TimeSeriesInterface | undefined =>
  timeSeries.find(
    (timeSeriesItem) =>
      timeSeriesItem.metric.type === metricType &&
      hasSameLabels(timeSeriesItem.metric.labels, metricLabels) &&
      timeSeriesItem.monitoredResource.labels?.some((label) => {
        const isCorrectLabel = label.key === monitoredResourceType;
        return monitoredResourceValue
          ? isCorrectLabel && label.value === monitoredResourceValue
          : isCorrectLabel;
      })
  );

export const getTimeSeriesListForMetricAndLabels = (
  timeSeries: TimeSeriesInterface[],
  monitoredResourceType: MonitoredResourceTypeLabel,
  metricType: string,
  monitoredResourceValue?: string,
  metricLabels?: LabelInterface[]
): TimeSeriesInterface[] | undefined =>
  timeSeries.filter(
    (timeSeriesItem) =>
      timeSeriesItem.metric.type === metricType &&
      hasSameLabels(timeSeriesItem.metric.labels, metricLabels) &&
      timeSeriesItem.monitoredResource.labels?.some((label) => {
        const isCorrectLabel = label.key === monitoredResourceType;
        return monitoredResourceValue
          ? isCorrectLabel && label.value === monitoredResourceValue
          : isCorrectLabel;
      })
  );

export const getTimeSeriesForMetricWithSameMonitoredResourceLabels = (
  timeSeries: TimeSeriesInterface[],
  monitoredResourceType: MonitoredResourceTypeLabel,
  metricType: string,
  monitoredResourceValue: string,
  metricLabels?: LabelInterface[]
): TimeSeriesInterface | undefined =>
  timeSeries.find(
    (timeSeriesItem) =>
      timeSeriesItem.metric.type === metricType &&
      hasSameLabels(timeSeriesItem.metric.labels, metricLabels) &&
      hasSameLabels(timeSeriesItem.monitoredResource.labels, [
        {
          key: monitoredResourceType,
          value: monitoredResourceValue
        }
      ])
  );

export const sortDataPointsByDateTimeFromDescending = (
  dataPoints: DataPointInterface[]
): DataPointInterface[] =>
  dataPoints
    .slice()
    .sort(
      (a, b) =>
        Number(new Date(b.interval.dateTimeFrom)) -
        Number(new Date(a.interval.dateTimeFrom))
    );
export const sortDataPointsByDateTimeFromAscending = (
  dataPoints: DataPointInterface[]
): DataPointInterface[] =>
  dataPoints
    .slice()
    .sort(
      (a, b) =>
        Number(new Date(a.interval.dateTimeFrom)) -
        Number(new Date(b.interval.dateTimeFrom))
    );

export const getLastDataPoint = (
  dataPoints: DataPointInterface[]
): DataPointInterface | undefined => {
  return sortDataPointsByDateTimeFromDescending(dataPoints).at(0);
};

const dataPointIntervalIsInMarketPeriod = (
  interval: DateTimeIntervalInterface,
  marketPeriod: MarketPeriodInterface
): boolean => {
  return moment(interval.dateTimeFrom).isBetween(
    marketPeriod.startDateTime,
    marketPeriod.endDateTime,
    undefined,
    '[]'
  );
};

export const getLastDataPointForCurrentMarketPeriod = (
  dataPoints: DataPointInterface[],
  marketPeriods: MarketPeriodInterface[]
): DataPointInterface | undefined => {
  const currentPeriod = getCurrentMarketPeriod(marketPeriods);
  if (currentPeriod == null) {
    return undefined;
  }
  const dataPointsFromActualPeriod = dataPoints?.filter((dataPoint) =>
    dataPointIntervalIsInMarketPeriod(dataPoint.interval, currentPeriod)
  );
  return sortDataPointsByDateTimeFromDescending(dataPointsFromActualPeriod).at(
    0
  );
};

export const getAirTemperatureValuesFromDataPoints = (
  dataPoints: DataPointInterface[] | undefined
): ChartPoint[] =>
  dataPoints?.map((dataPoint) => {
    return {
      x: dataPoint.interval.dateTimeFrom,
      y: toNumber(dataPoint.value.objectValue.airTemperature ?? 0),
      sourceQualityFlags: dataPoint.metadata.sourceQualityFlags
    };
  }) || [];

export const hasSameLabels = (
  labels: LabelInterface[] | undefined,
  searchLabels: LabelInterface[] | undefined
) => {
  if (
    (!labels && searchLabels && searchLabels.length === 0) ||
    (!searchLabels && labels && labels.length === 0)
  ) {
    return true;
  }

  return _.isEqual(labels, searchLabels);
};

// TODO - do not use local timzeone
export const getIncrementalAllDayTimeInterval = (startDateTime?: string) =>
  startDateTime
    ? {
        startDateTime: moment(startDateTime).format(
          DateFormat.REST_DATETIME_FORMAT
        ),
        endDateTime: moment(startDateTime)
          .endOf('day')
          .format(DateFormat.REST_DATETIME_FORMAT)
      }
    : getTimeIntervalForDay();

export const paginationForLastDataPoint: PaginationInterface = {
  timeOrder: BESortDirection.DESC,
  pageSize: 1
};

export const createHeaderLabels = (
  translate: TFunction,
  componentRoot: string,
  isBalanceGroupLevel = true
): string[] => {
  const headerLabels = isBalanceGroupLevel
    ? [
        translate(`${componentRoot}.columns.deliveryPointName`),
        translate(`${componentRoot}.columns.communicationStatus`)
      ]
    : [translate(`${componentRoot}.columns.deviceName`)];

  headerLabels.push(
    translate(`${componentRoot}.columns.actualProduction`),
    translate(`${componentRoot}.columns.plannedProduction`),
    translate(`${componentRoot}.columns.productionFulfillment`),
    translate(`${componentRoot}.columns.actions`)
  );

  return headerLabels;
};

export enum WaterFlowType {
  TURBINE = 'TURBINE',
  PASSIVE = 'PASSIVE'
}

export enum ModeType {
  P_MODE = 'P_MODE',
  Q_MODE = 'Q_MODE',
  WATER_LEVEL_MODE = 'WATER_LEVEL_MODE'
}

export enum EventType {
  LOCAL_DELIVERY_POINT_CONTROL = 'LOCAL_DELIVERY_POINT_CONTROL'
}

export const getMetricValueWithUnit = (
  value: string | number | undefined,
  unit: UnitType,
  t: TFunction
): string => {
  const unitText = t(`common.unitsOfMeasure.${unit}`);
  return value !== undefined
    ? `${parseFloat(
        toNumber(value).toFixed(FRACTION_DIGITS_THREE_DECIMAL)
      )} ${unitText}`
    : '';
};
export const canMetricHaveMultipleValuesForInterval = (
  metricType: string
): boolean => {
  const metricsWithMultipleValuesAllowed: string[] = [
    MetricValueType.infrastructureOperationEvent
  ];
  return metricsWithMultipleValuesAllowed.includes(metricType);
};

export const mergeDataPoints = (
  oldDataPoints: DataPointInterface[],
  newDataPoints: DataPointInterface[],
  compareDataPointValue = false
): DataPointInterface[] => {
  const getObjectForComparing = (dataPoint: DataPointInterface) => {
    return {
      interval: dataPoint.interval,
      ...(compareDataPointValue ? { value: dataPoint.value } : undefined)
    };
  };

  // filter old dataPoints without match in new dataPoints
  const filteredDataPoints = oldDataPoints.filter((oldDataPoint) => {
    const oldCompare = getObjectForComparing(oldDataPoint);
    if (
      newDataPoints.some((newDataPoint) => {
        const newCompare = getObjectForComparing(newDataPoint);
        return _.isEqual(oldCompare, newCompare);
      })
    ) {
      return false; // ignore item, keep newer item
    }
    return true;
  });

  return [...filteredDataPoints, ...newDataPoints];
};

export const transformTokWh = (value: number): number =>
  BigNumber(value).multipliedBy(1000).toNumber();

export const getActualProductionForDevice = (
  timeSeries: TimeSeriesInterface[],
  deviceId: string
): string | undefined => {
  const actualProduction = getLastDataPointForDeviceAndMetricType(
    timeSeries,
    deviceId,
    MetricValueType.activeEnergyBalance
  )?.value.doubleValue;

  if (actualProduction !== undefined) {
    return BigNumber(actualProduction).toString();
  }
};

export const createActiveEnergyForDeliveryPointForTimeSeriesQuery = (
  deliveryPointId: string | bigint,
  startTime?: string
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.activeEnergyBalance
    },
    monitoredResource: {
      type: MonitoredResourceType.DELIVERY_POINT,
      labels: getLabelsForMonitoredResourceTypeDeliveryPoint(deliveryPointId)
    },
    timeInterval: getIncrementalAllDayTimeInterval(startTime),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const createActiveEnergyForDeviceForTimeSeriesQuery = (
  deviceId: string,
  startTime?: string
): TimeSeriesQueryInterface => {
  return {
    metric: { type: MetricValueType.activeEnergyBalance },
    monitoredResource: {
      type: MonitoredResourceType.DEVICE,
      labels: getLabelsForMonitoredResourceTypeDevice(deviceId)
    },
    timeInterval: getIncrementalAllDayTimeInterval(startTime),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const getLabelsForMonitoredResourceTypeDeliveryPoint = (
  deliverPointId: string | bigint
) => {
  return [
    { key: MonitoredResourceTypeLabel.DELIVERY_POINT_ID, value: deliverPointId }
  ];
};

export const getLabelsForMonitoredResourceTypeLocalDistributionSystem = (
  localDistributionSystemId: string | bigint
) => {
  return [
    {
      key: MonitoredResourceTypeLabel.LOCAL_DISTRIBUTION_SYSTEM_ID,
      value: localDistributionSystemId
    }
  ];
};

export const getLabelsForMonitoredResourceTypeDevice = (
  deviceId: string | bigint
) => {
  return [{ key: MonitoredResourceTypeLabel.DEVICE_ID, value: deviceId }];
};

const mergeChartPointSum = (
  chartPoint1: ChartPoint,
  chartPoint2: ChartPoint
): ChartPoint => {
  const newSourceQualityFlags = new Set([
    ...(chartPoint1.sourceQualityFlags || []),
    ...(chartPoint2.sourceQualityFlags || [])
  ]);

  return {
    x: chartPoint1.x,
    y: chartPoint1.y + chartPoint2.y,
    sourceQualityFlags: Array.from(newSourceQualityFlags)
  };
};

export const getDataPointValueSumsFromTimeSeriesForChart = (
  timeSeries: TimeSeriesInterface[]
): ChartPoint[] => {
  const sumsResult: ChartPoint[] = [];

  timeSeries.forEach((timeSeriesItem) => {
    timeSeriesItem.dataPoints?.forEach((dataPoint) => {
      const x = dataPoint.interval.dateTimeFrom;
      const y = dataPoint.value.doubleValue;
      const sourceQualityFlags = dataPoint.metadata.sourceQualityFlags;

      const currentDataPoint = {
        x: x,
        y: y,
        sourceQualityFlags: sourceQualityFlags
      };

      let existingDataPointUpdated = false;
      for (let i = 0; i < sumsResult.length; i++) {
        // check if chart point for current time exists
        if (sumsResult[i].x === x) {
          const mergedChartPoint = mergeChartPointSum(
            sumsResult[i],
            currentDataPoint
          );

          sumsResult[i] = mergedChartPoint;
          existingDataPointUpdated = true;
          break;
        }
      }

      // if chart point for current time does not exist, just add it to the list
      if (!existingDataPointUpdated) {
        sumsResult.push(currentDataPoint);
      }
    });
  });

  return sumsResult;
};

export const trySafeConvertMWhToKWh = (value: string | undefined) => {
  if (value !== undefined) {
    const valueBigNumber = BigNumber(value);
    if (!valueBigNumber.isNaN()) {
      return valueBigNumber.multipliedBy(1000).toString();
    }
  }
};

export const getSumActualProductionForDeliveryPoint = (
  timeSeries: TimeSeriesInterface[],
  deviceIds: (string | bigint)[]
): string | undefined => {
  let sumActualProduction: BigNumber | undefined;

  deviceIds.forEach((deviceId) => {
    const lastDataPoint = getLastDataPointForDeviceAndMetricType(
      timeSeries,
      deviceId,
      MetricValueType.activeEnergyBalance
    );

    if (lastDataPoint?.value?.doubleValue) {
      const actualProductionBigNumber = BigNumber(
        lastDataPoint?.value.doubleValue
      );

      if (!actualProductionBigNumber.isNaN()) {
        if (sumActualProduction === undefined) {
          sumActualProduction = BigNumber(0);
        }

        sumActualProduction = sumActualProduction.plus(
          actualProductionBigNumber
        );
      }
    }
  });

  return sumActualProduction?.toString();
};

export const getPlannedProductionForDeviceForActualPeriod = (
  productionPlanList: GetDeviceProductionPlanDetailResponse[],
  marketPeriods: MarketPeriodInterface[],
  deviceId: string | bigint
) => {
  const plansForDevice = getPlansForDevice(productionPlanList, deviceId);

  const currentPeriod = getCurrentMarketPeriod(marketPeriods);
  if (!currentPeriod) return undefined;
  const currentPeriodNumber = currentPeriod?.timeUnitNumber;

  // get revision with largest revisionNumber
  const actualRevision = plansForDevice?.revisions
    ?.slice()
    ?.sort((a, b) => b.revisionNumber - a.revisionNumber) // DESCending sort
    .at(0);

  const periodProduction = actualRevision?.periods.find(
    (period) => period.periodNumber === currentPeriodNumber
  )?.production;

  if (periodProduction != null) {
    return BigNumber(periodProduction).toString();
  }
};

export const getSumPlannedProductionForDeliveryPoint = (
  productionPlanList: GetDeviceProductionPlanDetailResponse[],
  deviceIds: (string | bigint)[],
  marketPeriods: MarketPeriodInterface[]
): string | undefined => {
  let sumPlannedProduction: BigNumber | undefined;

  deviceIds.forEach((deviceId) => {
    const plannedProduction = getPlannedProductionForDeviceForActualPeriod(
      productionPlanList,
      marketPeriods,
      deviceId
    );

    if (plannedProduction !== undefined) {
      if (sumPlannedProduction === undefined) {
        sumPlannedProduction = BigNumber(0);
      }
      sumPlannedProduction = sumPlannedProduction.plus(plannedProduction);
    }
  });

  return sumPlannedProduction?.toString();
};

export const createDeviceActiveEnergyTimeSeriesQueryForMarketPeriod = (
  deviceId: string | bigint,
  marketPeriod: MarketPeriodInterface
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: MetricValueType.activeEnergyBalance
    },
    monitoredResource: {
      type: MonitoredResourceType.DEVICE,
      labels: [{ key: MonitoredResourceTypeLabel.DEVICE_ID, value: deviceId }]
    },
    timeInterval: getTimeIntervalFromMarketPeriod(marketPeriod),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

export const createDeliveryPointActiveEnergyTimeSeriesQueryForMarketPeriod = (
  metric: MetricSpecInterface,
  deliveryPointId: string | bigint,
  marketPeriod: MarketPeriodInterface
): TimeSeriesQueryInterface => {
  return {
    metric: {
      type: metric.type
    },
    monitoredResource: {
      type: getDeliveryPointResourceType(metric.monitoredResourceTypes),
      labels: getLabelsForMonitoredResourceTypeDeliveryPoint(deliveryPointId)
    },
    timeInterval: getTimeIntervalFromMarketPeriod(marketPeriod),
    transformationFilter: { unitOfMeasure: UnitType.MWH }
  };
};

const getPlansForDevice = (
  productionPlanList: GetDeviceProductionPlanDetailResponse[],
  deviceId: string | bigint
): GetDeviceProductionPlanDetailResponse | undefined =>
  productionPlanList.find((plan) => {
    return plan.deviceId === deviceId;
  });

export const parseSourceToKeyedObject = (source: string): KeyedObject[] => {
  const sourceItems = source.split('/');
  return sourceItems.map((item) => {
    const keyValue = item.split('@');
    return { [keyValue[0]]: keyValue[1] };
  });
};
